import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { breakpoint, sizes, colors } from 'variables';
import { SvgSprite } from 'modules/svgSprite';
import matchMedia from 'modules/helpers/matchMedia';
import history from 'modules/helpers/history';
import { useTranslation } from 'react-i18next';
import { ExpandSearch, SearchFor } from 'autogen/translation-keys/trans-website-search';
import { em } from './helpers/style';
import { Spacing16, Spacing8 } from 'autogen/design-tokens/tokens';
import { RelatedTagModel } from 'autogen/swagger/all';
import LinkAction from 'components/shared/Routing/components/LinkAction';

const Container = styled.div<IContainerProps>`
    align-items: baseline;

    ${breakpoint.up(sizes.xl)} {
        padding-bottom: 6px;

        .header__fixed-rel-tags & {
            padding-left: ${Spacing16};
            padding-right: ${Spacing16};
            margin: auto;
            max-width: 1680px;
        }
    }

    ${(props): string =>
        props.scrollable
            ? `
                display: flex;

                ${breakpoint.up(sizes.xl)} {
                    display: none;
                }`
            : `
                display: none;

                ${breakpoint.up(sizes.xl)} {
                    display: flex;
                }`};
`;

const Title = styled.span`
    display: none;

    ${breakpoint.up(sizes.xl)} {
        display: flex;
        align-items: center;
        font-weight: bold;
        white-space: nowrap;
        margin-right: ${Spacing16};
    }
`;

const WrapContainer = styled.div`
    position: relative;
    width: 100%;

    &:before,
    &:after {
        position: absolute;
        content: '';
        top: 0;
        height: 100%;
        width: 15px;
    }

    &:before {
        left: 0;
        background-image: linear-gradient(to left, rgba(255, 255, 255, 0), ${colors.negative});
        z-index: 1;
    }

    &:after {
        right: 0;
        background-image: linear-gradient(to right, rgba(255, 255, 255, 0), ${colors.negative});
    }

    ${breakpoint.up(sizes.xl)} {
        flex: 1;

        &:before,
        &:after {
            content: none;
        }
    }
`;

const OverflowContainer = styled.div<IOverflowProps>`
    overflow-x: scroll;

    ${breakpoint.up(sizes.xl)} {
        ${(props): string => props.folded && `height: ${em(37)};`};
        overflow: hidden;
    }
`;

const List = styled.ul<IOverflowProps>`
    position: relative;
    display: inline-flex;
    padding: 0;
    margin: 0 ${Spacing16};
    padding-bottom: 10px;

    ${breakpoint.up(sizes.xl)} {
        display: flex;
        flex-wrap: wrap;
        margin: 0;
        padding-bottom: 0;

        ${(props): string =>
            props.folded
                ? `
                    padding: 0 26px 0 0;
                    max-height: ${em(69)};
                `
                : `
                    padding: 0;
                    max-height: initial;
                `};
    }
`;

const Item = styled.li`
    height: ${em(32)};
    list-style-type: none;
    margin-right: 5px;
    border: 1px solid ${colors.color3};
    border-radius: 5px;
    background: ${colors.negative};

    &:last-child {
        margin-right: 0;
    }

    ${breakpoint.up(sizes.xl)} {
        margin-bottom: 5px;
    }
`;

const Anchor = styled(LinkAction)`
    position: relative;
    display: flex;
    padding: 5px 12px;
    white-space: nowrap;
    font-size: 1rem;
    padding-left: 22px;
    font-weight: bold;
    align-items: center;

    &:before {
        content: '+';
        position: absolute;
        top: 50%;
        left: 8px;
        font-size: 1.2rem;
        line-height: 0;
    }

    &:focus,
    &:active,
    &:hover {
        text-decoration: none;
    }
`;

const WrapButton = styled.button`
    position: absolute;
    border: 1px solid ${colors.color3};
    border-radius: 5px;
    background: ${colors.negative};
    height: ${em(32)};
    width: 26px;
    right: 0;
    bottom: ${em(37)};
`;

const SearchIcon = styled(SvgSprite)`
    width: 15px;
    height: 15px;
    margin-right: ${Spacing8};
    flex-shrink: 0;
`;

interface IContainerProps {
    scrollable: boolean;
}

interface IOverflowProps {
    folded: boolean;
}

interface IGroup {
    tag: string;
    suggestionPhrase: string;
    searchUrl: string;
}

interface IRelatedTags {
    tags: RelatedTagModel[];
    scrollable: boolean;
}

const RelatedTags = (props: IRelatedTags): JSX.Element => {
    const { tags, scrollable } = props;
    const { t } = useTranslation();

    const [isFolded, setIsFolded] = useState<boolean>(true);
    const [isDesktop, setIsDesktop] = useState<boolean>(matchMedia.isDesktop);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);

    const handleResize = (): void => setIsDesktop(matchMedia.isDesktop);

    const wrapper = useMemo(
        (): HTMLElement => typeof document !== 'undefined' && document.querySelector('.js-wrapper'),
        []
    );

    const toggleTags = (params) => {
        const urlParams = new URLSearchParams(params);
        if (urlParams.has('search-query')) {
            const keyLength = Array.from(urlParams.keys()).length;
            // TODO: this needs some refactoring, search string with spaces or our debug=1 counts as multiple parameters.
            if (keyLength > 1) {
                setIsDisabled(true);
                // disabled rel-tags style
                const nodeList = document.querySelectorAll('.has-rel-tags');
                Array.from(nodeList).forEach((node): void => {
                    requestAnimationFrame((): void => {
                        node.classList.remove('has-rel-tags');
                        node.classList.add('disabled-has-rel-tags');
                    });
                });
                // hide fixed header
                const fixedHeader = document.querySelector('.header__fixed-rel-tags') as HTMLElement;
                requestAnimationFrame((): void => {
                    fixedHeader.style.display = 'none';
                });
            } else {
                setIsDisabled(false);
                // enable rel-tags style
                const nodeList = document.querySelectorAll('.disabled-has-rel-tags');
                Array.from(nodeList).forEach((node): void => {
                    requestAnimationFrame((): void => {
                        node.classList.remove('disabled-has-rel-tags');
                        node.classList.add('has-rel-tags');
                    });
                });
                // show fixed header
                const fixedHeader = document.querySelector('.header__fixed-rel-tags') as HTMLElement;
                if (fixedHeader) {
                    requestAnimationFrame((): void => {
                        fixedHeader.style.display = 'block';
                    });
                }
            }
        }
    };

    const locationChanged = (event: any): void => {
        toggleTags(event.location.search);
    };

    const toggleFold = (): void => {
        setIsFolded(!isFolded);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        requestAnimationFrame((): void => {
            // add en extra offset to header if there's any tags
            if (!isDesktop && tags?.length) {
                wrapper.style.marginTop = '42px';
            } else {
                wrapper.style.marginTop = '';
            }
        });

        return () => window.removeEventListener('resize', handleResize);
    }, [tags, isDesktop, wrapper.style, handleResize]);

    useEffect(() => {
        toggleTags(window.location.search);

        const unobserveHistoryChanges = history.listen(locationChanged);

        return (): any => {
            unobserveHistoryChanges(); // call to unlisten
        };
    }, [locationChanged, toggleTags]);

    return (
        <Container scrollable={scrollable}>
            {tags && !!tags.length && !isDisabled && (
                <>
                    <Title>
                        <SearchIcon sprite="Search" />
                        {t(ExpandSearch)}
                    </Title>
                    <WrapContainer>
                        <OverflowContainer folded={isFolded}>
                            <List folded={isFolded}>
                                {tags.map((item): JSX.Element => {
                                    const { tag, suggestionPhrase, searchLinkAction } = item;

                                    return (
                                        <Item key={tag}>
                                            <Anchor
                                                action={searchLinkAction}
                                                title={`${t(SearchFor)} "${suggestionPhrase}"`}
                                                className="tracking_searchPage_relatedTagClick js-trackTextAsLabel"
                                            >
                                                {tag}
                                            </Anchor>
                                        </Item>
                                    );
                                })}
                                {isFolded && !scrollable && <WrapButton onClick={toggleFold}>...</WrapButton>}
                            </List>
                        </OverflowContainer>
                    </WrapContainer>
                </>
            )}
        </Container>
    );
};

export default RelatedTags;
